import React from 'react';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import type {
  TrendBrandRadarHit,
  TrendCategoryRunMode,
  TrendDiscoveryCandidate,
  TrendDiscoveryQuery,
  TrendDiscoveryRunState,
  TrendModuleSettings,
  TrendPipelineRunInputMode,
  TrendPipelineRunProfile,
  TrendPipelineRun,
  TrendPilotRunPreset,
  TrendRunPlan,
  TrendSource,
} from '../types';
import { extractTrendUiErrorMessage } from '../errorUtils';
import { TREND_COLORS } from '../trendTheme';
import { ACTIVE_RUN_STATUSES, STEP_LABELS } from './adminConstants';
import type { MultiValueDrafts } from './adminTypes';

export function joinMultiValue(values: string[]): string {
  return values.join(', ');
}

export function splitMultiValue(value: string): string[] {
  return value
    .split(/\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function getRunScopeLabel(run: Pick<TrendPipelineRun, 'scope'>): string {
  return run.scope === 'preview' ? 'Testläge' : 'Live';
}

export function getRunProfileLabel(profile: TrendPipelineRunProfile): string {
  if (profile === 'micro') return 'Mikrotest';
  if (profile === 'mini') return 'Minikörning';
  return 'Full körning';
}

export function getRunInputModeLabel(inputMode: TrendPipelineRunInputMode): string {
  if (inputMode === 'fixture') return 'Testdata';
  if (inputMode === 'source_items') return 'Valda källposter';
  return 'Livekällor';
}

export function getRunCategoryModeLabel(mode: TrendCategoryRunMode): string {
  if (mode === 'design_packaging') return 'Design & förpackning';
  if (mode === 'food_recipe') return 'Mat & recept';
  if (mode === 'market_competition') return 'Marknad & konkurrens';
  return 'Full körning';
}

export function getPlanModeChipLabel(plan: TrendRunPlan): string {
  if (plan.runMode === 'brand_radar') return 'Brand Radar';
  if (plan.runMode === 'report_only') return 'Endast rapport';
  if (plan.runMode === 'second_opinion_only') return 'Second opinion';
  if (plan.runMode === 'source_test') return 'Källtest';
  if (plan.runMode === 'prompt_test') return 'Prompttest';
  if (plan.runMode === 'model_test') return 'Modelltest';
  if (plan.categoryRunMode === 'full' && plan.publishMode === 'preview') return 'Testkörning';
  return getRunCategoryModeLabel(plan.categoryRunMode);
}

export function getPlanCostChipLabel(plan: TrendRunPlan): string {
  return `Kostnadstak ${plan.estimatedCost.max} ${plan.estimatedCost.currency}`;
}

export function getDiscoveryQueriesForPreset(
  preset: TrendPilotRunPreset,
  queries: TrendDiscoveryQuery[],
): TrendDiscoveryQuery[] {
  const activeQueries = queries.filter((query) => query.active);
  if (preset.key !== 'single_discovery_query_preview') {
    return activeQueries;
  }

  const categoryAllowlist = preset.plan.categoryAllowlist || [];
  if (categoryAllowlist.length === 0) {
    return activeQueries;
  }

  const allowedCategories = categoryAllowlist.map((category) => String(category));
  return activeQueries.filter((query) => allowedCategories.includes(query.category));
}

export function getSourceTestSources(sources: TrendSource[]): TrendSource[] {
  return sources
    .filter((source) => source.active)
    .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));
}

export function getPipelineRunModeChipLabel(run: TrendPipelineRun): string {
  if (run.runMode === 'brand_radar') return 'Brand Radar';
  if (run.runMode === 'report_only') return 'Endast rapport';
  if (run.runMode === 'second_opinion_only') return 'Second opinion';
  if (run.runMode === 'source_test') return 'Källtest';
  if (run.runMode === 'prompt_test') return 'Prompttest';
  if (run.runMode === 'model_test') return 'Modelltest';
  if (run.categoryRunMode === 'full' && run.scope === 'preview') return 'Testkörning';
  return getRunCategoryModeLabel(run.categoryRunMode);
}

export function buildMultiValueDrafts(settings: TrendModuleSettings | null): MultiValueDrafts {
  return {
    activeMarkets: joinMultiValue(settings?.fontanaContext.activeMarkets || []),
    priorityCategories: joinMultiValue(settings?.fontanaContext.priorityCategories || []),
    priorityIngredients: joinMultiValue(settings?.fontanaContext.priorityIngredients || []),
    recipeFocus: joinMultiValue(settings?.fontanaContext.recipeFocus || []),
    growthGoals: joinMultiValue(settings?.fontanaContext.growthGoals || []),
    salesChannels: joinMultiValue(settings?.fontanaContext.salesChannels || []),
    dvhFocus: joinMultiValue(settings?.fontanaContext.dvhFocus || []),
    foodserviceFocus: joinMultiValue(settings?.fontanaContext.foodserviceFocus || []),
    avoidThemes: joinMultiValue(settings?.fontanaContext.avoidThemes || []),
    prioritizedThemes: joinMultiValue(settings?.sourceStrategy.prioritizedThemes || []),
    socialPilotTerms: joinMultiValue(settings?.sourceStrategy.socialPilotTerms || []),
    designFocusAreas: joinMultiValue(settings?.sourceStrategy.designFocusAreas || []),
    consumerInsightFocus: joinMultiValue(settings?.sourceStrategy.consumerInsightFocus || []),
  };
}

export function mapFriendlyRecommendedError(err: any): string {
  const rawMessage = extractTrendUiErrorMessage(err, '');
  if (/endpoint not found/i.test(rawMessage)) {
    return 'Rekommenderade källor kunde inte hämtas från backend just nu. Uppdatera sidan eller prova igen om någon minut.';
  }
  return rawMessage || 'Rekommenderade källor kunde inte laddas just nu.';
}

export function mapFriendlyActionError(err: any, fallback: string): string {
  const status = Number(err?.response?.status || 0);
  const responseMessage = extractTrendUiErrorMessage(
    { response: { data: { message: err?.response?.data?.message } } },
    '',
  );
  const responseError = extractTrendUiErrorMessage(
    { response: { data: { error: err?.response?.data?.error } } },
    '',
  );
  const fallbackMessage = extractTrendUiErrorMessage(err, fallback);

  if (status === 409) {
    return responseMessage || 'En trendkörning pågår redan. Vänta tills den är klar innan du startar en ny.';
  }
  if (status === 502 || status === 504) {
    return 'Servern hann inte ge ett giltigt svar. Kontrollera backend/proxy och prova igen.';
  }
  return (
    (/^Failed to /i.test(responseMessage) ? '' : responseMessage)
    || responseError
    || fallbackMessage
  );
}

export function RunStatusIcon({ status }: { status: string }) {
  if (ACTIVE_RUN_STATUSES.has(status)) return <ScheduleIcon sx={{ color: TREND_COLORS.gold, fontSize: 20 }} />;
  if (status === 'completed') return <CheckCircleOutlineIcon sx={{ color: TREND_COLORS.olive, fontSize: 20 }} />;
  if (status === 'cancelled') return <ErrorOutlineIcon sx={{ color: TREND_COLORS.textMuted, fontSize: 20 }} />;
  if (status === 'failed') return <ErrorOutlineIcon sx={{ color: '#C53030', fontSize: 20 }} />;
  return <ScheduleIcon sx={{ color: TREND_COLORS.textMuted, fontSize: 20 }} />;
}

export function formatRunStep(run: TrendPipelineRun): string {
  const message = run.progress?.message?.trim();
  const current = run.progress?.current;
  const total = run.progress?.total;

  if (message) {
    if (typeof current === 'number' && typeof total === 'number') {
      return `${message} ${current}/${total}`;
    }
    return message;
  }

  return STEP_LABELS[run.lastStep] || run.lastStep || run.status;
}

export function formatRelativeRunUpdate(updatedAt?: string | null): string | null {
  if (!updatedAt) return null;

  const updatedMs = new Date(updatedAt).getTime();
  if (!Number.isFinite(updatedMs)) return null;

  const diffMs = Date.now() - updatedMs;
  if (diffMs < 0) return 'Uppdaterad nyss';
  if (diffMs < 60_000) return 'Uppdaterad nyss';

  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes === 1) return 'Uppdaterad för 1 minut sedan';
  if (diffMinutes < 60) return `Uppdaterad för ${diffMinutes} minuter sedan`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours === 1) return 'Uppdaterad för 1 timme sedan';
  if (diffHours < 24) return `Uppdaterad för ${diffHours} timmar sedan`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Uppdaterad för 1 dag sedan';
  return `Uppdaterad för ${diffDays} dagar sedan`;
}

export function formatRelativeRunAge(value?: string | null): string | null {
  const label = formatRelativeRunUpdate(value);
  if (!label) return null;
  return label.replace(/^Uppdaterad\s+/i, '');
}

export function getTimestampAgeMs(value?: string | null): number | null {
  if (!value) return null;
  const timestampMs = new Date(value).getTime();
  if (!Number.isFinite(timestampMs)) return null;
  return Date.now() - timestampMs;
}

export function getDiscoverySummaryNumber(summary: TrendDiscoveryRunState['summary'], key: string): number {
  if (!summary) return 0;
  const value = (summary as unknown as Record<string, unknown>)[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function discoveryStatusLabel(status: TrendDiscoveryCandidate['status']): string {
  if (status === 'approved') return 'Godkänd';
  if (status === 'rejected') return 'Avvisad';
  if (status === 'quarantined') return 'Karantän';
  if (status === 'needs_validation') return 'Behöver validering';
  if (status === 'inspiration_only') return 'Inspiration';
  if (status === 'imported') return 'Importerad';
  return 'Att granska';
}

export function discoveryStatusColor(status: TrendDiscoveryCandidate['status']): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (status === 'approved' || status === 'imported') return 'success';
  if (status === 'quarantined' || status === 'needs_validation' || status === 'inspiration_only') return 'warning';
  if (status === 'rejected') return 'error';
  return 'info';
}

export function discoveryLaneLabel(lane: TrendDiscoveryCandidate['lane'] | string): string {
  if (lane === 'curated_feed') return 'Kuraterade källor';
  if (lane === 'targeted_domain_crawl') return 'Riktad domäncrawl';
  if (lane === 'manual_import') return 'Manuell import';
  return 'AI-webbsök';
}

export function discoveryLaneColor(lane: TrendDiscoveryCandidate['lane'] | string): 'default' | 'success' | 'warning' | 'info' {
  if (lane === 'targeted_domain_crawl') return 'warning';
  if (lane === 'manual_import') return 'success';
  if (lane === 'curated_feed') return 'default';
  return 'info';
}

export function discoveryValidationLabel(status: TrendDiscoveryCandidate['validationStatus']): string {
  if (status === 'validated') return 'Validerande källa';
  if (status === 'needs_ab_validation') return 'Kräver A/B-validering';
  if (status === 'weak_support') return 'Svagt stöd';
  return 'Kräver källgranskning';
}

export function discoveryValidationColor(status: TrendDiscoveryCandidate['validationStatus']): 'success' | 'warning' | 'default' | 'info' {
  if (status === 'validated') return 'success';
  if (status === 'needs_ab_validation') return 'warning';
  if (status === 'weak_support') return 'default';
  return 'info';
}

export function sourceGeographyLabel(value: string): string {
  if (value === 'sweden') return 'Sverige';
  if (value === 'nordics') return 'Norden';
  if (value === 'europe') return 'Europa';
  if (value === 'usa_uk') return 'USA/UK';
  if (value === 'global') return 'Global';
  return 'Okänd geografi';
}

export function sourceTypeLabel(value: string): string {
  if (value === 'official_statistics') return 'Statistik/rapport';
  if (value === 'retail_chain') return 'Retail/kedja';
  if (value === 'foodservice') return 'Foodservice';
  if (value === 'packaging') return 'Förpackning';
  if (value === 'design_media') return 'Designmedia';
  if (value === 'social_recipe') return 'Social/recept';
  if (value === 'authority') return 'Myndighet';
  if (value === 'blog') return 'Blogg';
  if (value === 'industry_media') return 'Branschmedia';
  return 'Annan källa';
}

export function brandRadarSentimentLabel(value: TrendBrandRadarHit['sentiment']): string {
  if (value === 'positive') return 'Positiv';
  if (value === 'negative') return 'Negativ';
  if (value === 'mixed') return 'Blandad';
  return 'Neutral';
}

export function brandRadarClassificationLabel(value: TrendBrandRadarHit['classification']): string {
  if (value === 'risk') return 'Risk';
  if (value === 'opportunity') return 'Möjlighet';
  return 'Ingen åtgärd';
}

export function brandRadarClassificationActionLabel(value: TrendBrandRadarHit['classification']): string {
  if (value === 'risk') return 'Klassa som risk';
  if (value === 'opportunity') return 'Klassa som möjlighet';
  return 'Ingen åtgärd behövs';
}

export function brandRadarClassificationColor(value: TrendBrandRadarHit['classification']): 'error' | 'success' | 'default' {
  if (value === 'risk') return 'error';
  if (value === 'opportunity') return 'success';
  return 'default';
}

export function coverageStatusLabel(status: string): string {
  if (status === 'covered') return 'Täckt';
  if (status === 'partial') return 'Delvis';
  return 'Saknas';
}

export function coverageStatusColor(status: string): 'default' | 'success' | 'warning' {
  if (status === 'covered') return 'success';
  if (status === 'partial') return 'warning';
  return 'default';
}

export function formatShortDate(value?: string | null): string {
  if (!value) return 'Utan datum';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Utan datum';
  return date.toISOString().slice(0, 10);
}

export function getSourceHealthLabel(source: TrendSource): string {
  if (source.sourceHealthScore === null || source.sourceHealthScore === undefined) return 'Ej källtestad';
  if (source.sourceHealthScore >= 75) return 'Bra källa';
  if (source.sourceHealthScore >= 45) return 'Behöver justeras';
  return 'Ger mest brus';
}

export function getSourceHealthColor(source: TrendSource): 'default' | 'success' | 'warning' | 'error' {
  if (source.sourceHealthScore === null || source.sourceHealthScore === undefined) return 'default';
  if (source.sourceHealthScore >= 75) return 'success';
  if (source.sourceHealthScore >= 45) return 'warning';
  return 'error';
}

export function formatSourceHealthScore(source: TrendSource): string {
  if (source.sourceHealthScore === null || source.sourceHealthScore === undefined) return 'Källhälsa: ej testad';
  return `Källhälsa: ${Math.round(source.sourceHealthScore)}/100`;
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${Math.round(bytes)} B`;
}
